import { Action } from '@ngrx/store';
import {
  AutoprintJobContext,
  AutoprintOrigin,
  AutoprintQueueElement,
  AutoprintSubscriptions,
  PrinterInfo,
} from '../printers';

export enum PrintersActionType {
  InitializeState = '[printers] InitializeState',
  InitializeStateSucess = '[printers] InitializeStateSucess',
  InitializeStateFailed = '[printers] InitializeStateFailed',
  AddPrinter = '[printers] AddPrinter',
  RemovePrinter = '[printers] RemovePrinter',
  TogglePrinterAutoStatus = '[printers] TogglePrinterAutoStatus',
  OrderDirectPrint = '[printers] OrderDirectPrint',
  PrintToEnabledPrinters = '[printers] PrintToEnabledPrinters',
  SaveBlobToStorageAsPDFAndPrint = '[printers] SaveBlobToStorageAsPDFAndPrint',
  HandlePrintJobs = '[printers] HandlePrintJobs',
  PrintJob = '[printers] PrintJob',
  SinglePrintJob = '[printers] SinglePrintJob',
  PrintJobSuccess = '[printers] PrintJobSuccess',
  PrintJobFailed = '[printers] PrintJobFailed',
  UpdateAutoprintSubscriptions = '[printers] UpdateAutoprintSubscriptions',
  UpdateAutoprintQueue = '[printers] UpdateAutoprintQueue',
  MarkOrderPrinted = '[printers] MarkOrderPrinted',
  RecordAutoprintAttempt = '[printers] RecordAutoprintAttempt',
  AutoprintAppStateChanged = '[printers] AutoprintAppStateChanged',
  AutoprintStartup = '[printers] AutoprintStartup',
  ReconcileAutoprintQueue = '[printers] ReconcileAutoprintQueue',
  DownloadOrderPdfAndPrint = '[printers] DownloadOrderPdfAndPrint',
}

export class InitializeState implements Action {
  readonly type = PrintersActionType.InitializeState;

  constructor() {}
}

export class InitializeStateSucess implements Action {
  readonly type = PrintersActionType.InitializeStateSucess;

  constructor(public readonly printersEnabled: []) {}
}

export class InitializeStateFailed implements Action {
  readonly type = PrintersActionType.InitializeStateFailed;

  constructor(public readonly error: any) {}
}

export class AddPrinter implements Action {
  readonly type = PrintersActionType.AddPrinter;

  constructor(public readonly newPrinter: PrinterInfo) {}
}

export class RemovePrinter implements Action {
  readonly type = PrintersActionType.RemovePrinter;

  constructor(public readonly url: string) {}
}

export class TogglePrinterAutoStatus implements Action {
  readonly type = PrintersActionType.TogglePrinterAutoStatus;

  constructor(
    public readonly url: string,
    public readonly newStatus: boolean,
  ) {}
}

export class OrderDirectPrint implements Action {
  readonly type = PrintersActionType.OrderDirectPrint;

  /**
   * `autoprintOrigin` names the automatic path that dispatched it — the order
   * push or foreground reconciliation — and is what lets the autoprint queue
   * drop a second copy of a receipt it already has (see
   * `PrinterService.addToAutoPrintQueue`). Left out, the order is printed
   * exactly as before. Card #4514.
   */
  constructor(
    public readonly message: any,
    public readonly storeId: number,
    public readonly autoprintOrigin?: AutoprintOrigin,
    /** Reconciliation only: see `AutoprintJobContext.printers`. */
    public readonly autoprintPrinters?: string[],
  ) {}
}

export class PrintToEnabledPrinters implements Action {
  readonly type = PrintersActionType.PrintToEnabledPrinters;

  constructor(
    public readonly contentUrl: string,
    public readonly storeId: number,
    public readonly orderUuid: string,
    /** Carried down to `PrintJob`; see `OrderDirectPrint`. Card #4514. */
    public readonly autoprint?: AutoprintJobContext,
  ) {}
}

export class HandlePrintJobs implements Action {
  readonly type = PrintersActionType.HandlePrintJobs;

  constructor(
    public readonly contentUrl: string,
    public readonly toPrints: PrinterInfo[],
    public readonly storeId: number,
    public readonly orderUuid: string,
    /** Carried down to `PrintJob`; see `OrderDirectPrint`. Card #4514. */
    public readonly autoprint?: AutoprintJobContext,
  ) {}
}

export class SaveBlobToStorageAsPDFAndPrint implements Action {
  readonly type = PrintersActionType.SaveBlobToStorageAsPDFAndPrint;

  constructor(
    public readonly blob: Blob,
    public readonly filename: string,
    public readonly mode: string,
    public readonly selectedPrinter: PrinterInfo,
    public readonly storeId?: number,
    public readonly orderUuid?: string,
    /** Carried down to `PrintJob`; see `OrderDirectPrint`. Card #4514. */
    public readonly autoprint?: AutoprintJobContext,
  ) {}
}

export class PrintJob implements Action {
  readonly type = PrintersActionType.PrintJob;

  constructor(
    public readonly contentUrl: string,
    public readonly printer: PrinterInfo,
    public readonly toPrints: PrinterInfo[],
    public readonly storeId: number,
    public readonly orderUuid: string,
    /** See `OrderDirectPrint`. Card #4514. */
    public readonly autoprint?: AutoprintJobContext,
  ) {}
}

export class SinglePrintJob implements Action {
  readonly type = PrintersActionType.SinglePrintJob;

  constructor(
    public readonly contentUrl: string,
    public readonly printer: PrinterInfo,
    public readonly storeId: number,
    public readonly orderUuid: string,
  ) {}
}

export class PrintJobSuccess implements Action {
  readonly type = PrintersActionType.PrintJobSuccess;

  constructor() {}
}

export class PrintJobFailed implements Action {
  readonly type = PrintersActionType.PrintJobFailed;

  constructor() {}
}

export class UpdateAutoprintSubscriptions implements Action {
  readonly type = PrintersActionType.UpdateAutoprintSubscriptions;

  constructor(public readonly subscriptions: AutoprintSubscriptions) {}
}

export class UpdateAutoprintQueue implements Action {
  readonly type = PrintersActionType.UpdateAutoprintQueue;

  constructor(public readonly autoprintQueue: AutoprintQueueElement[]) {}
}

/**
 * One order's receipt reached a printer.
 *
 * Dispatched only from `drainAutoPrintQueue`'s success path, never when the
 * print failed — a failed order has to stay eligible for foreground
 * reconciliation to retry. Card #4514.
 */
export class MarkOrderPrinted implements Action {
  readonly type = PrintersActionType.MarkOrderPrinted;

  /**
   * `printedAt` is supplied by the caller rather than read in the reducer so the
   * reducer stays pure, and so the record can be aged out on the same horizon
   * reconciliation looks back over. `printer` is the `printerKey` of the
   * printer that consumed the receipt, and `version` the order version the job
   * was queued with, when known. Card #4514.
   */
  constructor(
    public readonly orderId: string,
    public readonly printedAt: string = new Date().toISOString(),
    public readonly printer?: string,
    public readonly version?: number,
  ) {}
}

/**
 * An automatic job for one order on one printer was queued, or its attempt
 * failed. Recorded next to the printed receipts so reconciliation knows which
 * printers still owe the order a receipt — and which were never asked for it.
 * Card #4514.
 */
export class RecordAutoprintAttempt implements Action {
  readonly type = PrintersActionType.RecordAutoprintAttempt;

  constructor(
    public readonly orderId: string,
    public readonly state: 'queued' | 'failed',
    public readonly printer: string,
    public readonly version?: number,
    public readonly at: string = new Date().toISOString(),
  ) {}
}

/**
 * GoAdmin moved to or from the foreground.
 *
 * Going away records the checkpoint that bounds the background window; coming
 * back reconciles it, re-queueing the orders that arrived while the app was
 * gone and never reached a printer. Card #4514.
 */
export class AutoprintAppStateChanged implements Action {
  readonly type = PrintersActionType.AutoprintAppStateChanged;

  constructor(public readonly isActive: boolean) {}
}

/**
 * The printers effects have just been registered. If the app is already in the
 * foreground — a cold start, which Android reports before anything is
 * listening — it is treated as having just come back to it. Card #4514.
 */
export class AutoprintStartup implements Action {
  readonly type = PrintersActionType.AutoprintStartup;
}

/**
 * Reconcile again while the app is in the foreground: the follow-up pass after
 * the settle band. Card #4514.
 */
export class ReconcileAutoprintQueue implements Action {
  readonly type = PrintersActionType.ReconcileAutoprintQueue;
}

export class DownloadOrderPdfAndPrint implements Action {
  readonly type = PrintersActionType.DownloadOrderPdfAndPrint;

  constructor(
    public readonly storeId: number,
    public readonly orderId: string,
    public readonly uiLanguageLocale: string,
    public readonly catalogLanguageLocale: string,
    public readonly printer: PrinterInfo,
  ) {}
}

export type PrintersAction =
  | InitializeState
  | InitializeStateSucess
  | InitializeStateFailed
  | AddPrinter
  | RemovePrinter
  | TogglePrinterAutoStatus
  | OrderDirectPrint
  | PrintToEnabledPrinters
  | SaveBlobToStorageAsPDFAndPrint
  | HandlePrintJobs
  | PrintJob
  | SinglePrintJob
  | PrintJobSuccess
  | PrintJobFailed
  | UpdateAutoprintSubscriptions
  | UpdateAutoprintQueue
  | MarkOrderPrinted
  | RecordAutoprintAttempt
  | AutoprintAppStateChanged
  | AutoprintStartup
  | ReconcileAutoprintQueue
  | DownloadOrderPdfAndPrint;
